import { useState } from "react";
import {
  Paper,
  Text,
  Group,
  Badge,
  Button,
  Stack,
  Modal,
  Alert,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import {
  useGetVersionInfo,
  usePublishVersion,
  useGetCurrentVersion,
} from "../../api/translations";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

function VersionManagement() {
  const queryClient = useQueryClient();
  const { data: versionInfo, isLoading } = useGetVersionInfo();
  const { data: currentVersion } = useGetCurrentVersion();
  const { mutate: publishVersion, isPending } = usePublishVersion();
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);

  const hasUnpublishedChanges = versionInfo?.changesCount > 0;
  const nextVersion = versionInfo?.version || 0;

  const handlePublish = () => {
    setConfirmModalOpened(true);
  };

  const handleConfirmPublish = () => {
    publishVersion(
      {},
      {
        onSuccess: () => {
          setConfirmModalOpened(false);
          queryClient.invalidateQueries({ queryKey: ["translations"] });
        },
      }
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return dayjs(dateString).format("MMM D, YYYY");
  };

  if (isLoading) {
    return (
      <Paper p="md" withBorder>
        <Text>Loading version info...</Text>
      </Paper>
    );
  }

  return (
    <>
      <Paper p="md" withBorder radius="md" mt="md" mb="lg">
        <Group justify="space-between" align="center" mb="md">
          <Group gap="sm" align="center">
            <Text size="xl" fw={700}>
              v{currentVersion.version || 0}
            </Text>

            {hasUnpublishedChanges && (
              <Badge color="orange" size="lg" variant="filled" radius="sm">
                {versionInfo.changesCount} PENDING
              </Badge>
            )}
          </Group>

          {versionInfo?.lastPublishedDate && (
            <Group gap="md" align="center">
              <Text size="sm" c="dimmed">
                {formatDate(versionInfo.lastPublishedDate)}
              </Text>

              {versionInfo?.lastPublishedBy && (
                <>
                  <Text size="sm" c="dimmed">
                    ·
                  </Text>
                  <Text size="sm" c="dimmed">
                    {versionInfo.lastPublishedBy.firstName}{" "}
                    {versionInfo.lastPublishedBy.lastName}
                  </Text>
                </>
              )}

              <Button
                onClick={handlePublish}
                disabled={!hasUnpublishedChanges}
                loading={isPending}
                size="sm"
                leftSection={<IconAlertCircle size={16} />}
              >
                Publish
              </Button>
            </Group>
          )}

          {!versionInfo?.lastPublishedDate && (
            <Button
              onClick={handlePublish}
              disabled={!hasUnpublishedChanges}
              loading={isPending}
              size="sm"
              leftSection={<IconAlertCircle size={16} />}
            >
              Publish
            </Button>
          )}
        </Group>
      </Paper>

      <Modal
        opened={confirmModalOpened}
        onClose={() => setConfirmModalOpened(false)}
        title="Publish Translation Changes"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            This will update all translations in the mobile app to version{" "}
            <strong>v{nextVersion}</strong>.
          </Text>

          <Text size="sm">
            Are you sure you want to publish{" "}
            <strong>{versionInfo?.changesCount}</strong>{" "}
            {versionInfo?.changesCount === 1 ? "change" : "changes"}?
          </Text>

          <Alert icon={<IconAlertCircle size={16} />} color="yellow">
            This action cannot be undone. All mobile app users will receive the
            updated translations.
          </Alert>

          <Group justify="flex-end" mt="md">
            <Button
              variant="default"
              onClick={() => setConfirmModalOpened(false)}
            >
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={handleConfirmPublish}
              loading={isPending}
            >
              Publish v{nextVersion}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default VersionManagement;
